import { create } from "zustand";
import { persist, createJSONStorage, StateStorage } from "zustand/middleware";

/**
 * Authentication State Interface
 */
interface AuthState {
  // Authentication status
  isAuthenticated: boolean;
  isLoading: boolean;
  // User data
  wallet: string | null;
  token: string | null;

  // Error state
  error: string | null;

  // Session metadata
  jti: string | null;
  sid: string | null;
  expiresAt: number | null;

  // Token verification metadata
  lastVerified: number | null;

  // Actions
  setAuthenticated: (
    wallet: string,
    token: string,
    jti: string,
    sid: string,
    expiresIn: number,
  ) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearAuth: () => void;
  isTokenExpired: () => boolean;
  setLastVerified: (timestamp: number) => void;
  shouldVerifyToken: () => boolean;
}

/**
 * Minimum time between token verification attempts (5 minutes)
 * Prevents unnecessary API calls on rapid re-mounts
 */
const TOKEN_VERIFICATION_INTERVAL = 5 * 60 * 1000;

/**
 * Custom sessionStorage implementation with error handling
 * Gracefully handles quota exceeded and corrupted data
 */
const customStorage: StateStorage = {
  getItem: (name: string): string | null => {
    try {
      const item = sessionStorage.getItem(name);
      return item;
    } catch (error) {
      console.error("Failed to read from sessionStorage:", error);
      // Clear corrupted data
      try {
        sessionStorage.removeItem(name);
      } catch {
        // Silently fail if we can't even remove
      }
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      sessionStorage.setItem(name, value);
    } catch (error) {
      // Handle QuotaExceededError
      if (
        error instanceof DOMException &&
        (error.code === 22 ||
          error.code === 1014 ||
          error.name === "QuotaExceededError" ||
          error.name === "NS_ERROR_DOM_QUOTA_REACHED")
      ) {
        console.error("sessionStorage quota exceeded. Clearing old auth data.");
        // Try to clear auth storage to make room
        try {
          sessionStorage.removeItem(name);
          // Retry once after clearing
          sessionStorage.setItem(name, value);
        } catch (retryError) {
          console.error(
            "Failed to save auth data even after clearing:",
            retryError,
          );
        }
      } else {
        console.error("Failed to write to sessionStorage:", error);
      }
    }
  },
  removeItem: (name: string): void => {
    try {
      sessionStorage.removeItem(name);
    } catch (error) {
      console.error("Failed to remove from sessionStorage:", error);
    }
  },
};

/**
 * Auth Store
 * Manages authentication state including JWT tokens and wallet address
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      isLoading: false,
      wallet: null,
      token: null,
      error: null,
      jti: null,
      sid: null,
      expiresAt: null,
      lastVerified: null,

      // Set authenticated state with token
      setAuthenticated: (wallet, token, jti, sid, expiresIn) => {
        const expiresAt = Date.now() + expiresIn * 1000;
        set({
          isAuthenticated: true,
          isLoading: false,
          wallet,
          token,
          jti,
          sid,
          expiresAt,
          error: null,
          lastVerified: Date.now(),
        });
      },

      // Set loading state
      setLoading: (loading) => set({ isLoading: loading }),

      // Set error state
      setError: (error) => set({ error, isLoading: false }),

      // Clear authentication state
      clearAuth: () =>
        set({
          isAuthenticated: false,
          isLoading: false,
          wallet: null,
          token: null,
          jti: null,
          sid: null,
          expiresAt: null,
          error: null,
          lastVerified: null,
        }),

      // Check if token is expired
      isTokenExpired: () => {
        const { expiresAt } = get();
        if (!expiresAt) return true;
        return Date.now() >= expiresAt;
      },

      // Set last verification timestamp
      setLastVerified: (timestamp) => set({ lastVerified: timestamp }),

      // Check if token should be verified
      // Returns true if token exists and hasn't been verified recently
      shouldVerifyToken: () => {
        const { lastVerified, token, isTokenExpired } = get();

        // No token or already expired locally
        if (!token || isTokenExpired()) {
          return false;
        }

        // Never verified before
        if (!lastVerified) {
          return true;
        }

        // Check if enough time has passed since last verification
        const timeSinceLastVerification = Date.now() - lastVerified;
        return timeSinceLastVerification >= TOKEN_VERIFICATION_INTERVAL;
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => customStorage),
      // Only persist essential data
      partialize: (state) => ({
        wallet: state.wallet,
        token: state.token,
        jti: state.jti,
        sid: state.sid,
        expiresAt: state.expiresAt,
        isAuthenticated: state.isAuthenticated,
        lastVerified: state.lastVerified,
      }),
      // Handle corrupted JSON gracefully
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error("Failed to rehydrate auth state:", error);
            // Clear corrupted state
            try {
              sessionStorage.removeItem("auth-storage");
            } catch {
              // Silently fail
            }
          } else if (state) {
            // Validate rehydrated state has proper structure
            if (typeof state.token !== "string" && state.token !== null) {
              console.error("Invalid auth state detected, clearing");
              state.clearAuth();
              return;
            }

            // Check if token is expired during hydration
            // This ensures stale auth state is cleared BEFORE first render,
            // preventing ProtectedRoute from showing protected content briefly
            if (state.isAuthenticated && state.expiresAt) {
              if (Date.now() >= state.expiresAt) {
                console.log(
                  "Token expired during hydration, clearing auth state",
                );
                state.clearAuth();
              }
            }
          }
        };
      },
    },
  ),
);
